import { type DragEvent, useState } from "react";
import type { ActivityType } from "../../hooks/useActivityTypes.js";
import { PaletteSearch } from "./PaletteSearch.js";

const CATEGORY_ORDER = ["AI", "Data", "Integration", "Communication", "Transform", "Custom"];

export function NodePalette({
	types,
	loading,
	error,
	onRetry,
}: {
	types: ActivityType[];
	loading: boolean;
	error: string | null;
	onRetry: () => void;
}) {
	const [search, setSearch] = useState("");

	const filtered = types.filter(
		(t) =>
			t.type.includes(search.toLowerCase()) ||
			t.display_name.toLowerCase().includes(search.toLowerCase()),
	);

	const grouped = new Map<string, ActivityType[]>();
	for (const t of filtered) {
		if (!grouped.has(t.category)) grouped.set(t.category, []);
		grouped.get(t.category)?.push(t);
	}

	const onDragStart = (e: DragEvent, activityType: string) => {
		e.dataTransfer.setData("application/forge-activity", activityType);
		e.dataTransfer.effectAllowed = "move";
	};

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center">
				<span className="text-sm text-text-muted">Loading activities...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full flex-col items-center justify-center gap-3 p-4">
				<span className="text-sm text-error">Could not load activities</span>
				<button
					type="button"
					onClick={onRetry}
					className="rounded-xl bg-canvas px-3 py-1 text-xs text-text-primary hover:bg-border"
				>
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<PaletteSearch value={search} onChange={setSearch} />
			<div className="flex-1 overflow-y-auto px-3 pb-3">
				{CATEGORY_ORDER.filter((cat) => grouped.has(cat)).map((category) => (
					<div key={category} className="mb-3">
						<div className="mb-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
							{category}
						</div>
						{grouped.get(category)?.map((activity) => (
							<div
								key={activity.type}
								draggable
								onDragStart={(e) => onDragStart(e, activity.type)}
								className="mb-1.5 cursor-grab rounded-xl border border-border bg-canvas px-3 py-2 transition-colors duration-100 hover:border-accent active:cursor-grabbing"
							>
								<div className="text-sm text-text-primary">{activity.display_name}</div>
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	);
}
